'use client';

import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { arePathsEqual } from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import { usePathname } from 'next/navigation';

export type BottomBarProps = {
  className?: {
    root?: string;
  };
  items?: BottomBarItemProps[];
};

export function BottomBar({ items = HEADER_CONFIG.bottomBar.items, ...props }: BottomBarProps) {
  return (
    <div
      className={cn(
        'fixed bottom-0 left-0 right-0 top-auto z-50 flex h-[58px] flex-row  items-center justify-between gap-0 bg-background px-4 pt-2 shadow-[0px_0px_15px_rgba(0,0,0,0.1)] sm:hidden',
        'ea-bottom-bar',
        props.className?.root,
      )}
    >
      {items?.map(({ key, ...item }) => (
        <BottomBarItem key={key} {...item} />
      ))}
    </div>
  );
}

export type BottomBarItemProps = {
  key: string;
  icon?: React.ReactNode;
  label?: string;
  className?: {
    root?: string;
    iconContainer?: string;
    label?: string;
    active?: string;
  };
  href: string | (() => string);
};
export function BottomBarItem(props: BottomBarItemProps) {
  if (!props.icon && !props.label) {
    throw new Error('Either icon or label must be provided');
  }
  const pathname = usePathname();
  let href = typeof props.href === 'function' ? props.href() : props.href;
  const isActive = arePathsEqual(pathname, href);
  const me = useCustomerStore(s => s.me);
  const isCustomerStoreInitialized = useCustomerStore(s => s.isInitialized);

  if (
    !me &&
    isCustomerStoreInitialized &&
    (href === ROUTES.PROFILE() || href === ROUTES.FAVORITES())
  ) {
    href = ROUTES.SIGN_IN();
  }

  const { t } = useTranslations();

  return (
    <Link
      href={href ?? '#'}
      className={cn(
        'flex h-[50px] w-[70px] flex-col items-center justify-center gap-1 rounded-[4px] pb-[2px]',
        'ea-bottom-bar__item text-black',
        props.className?.root,
        {
          'ea-bottom-bar__item--active bg-[#FAF6E5] text-primary': isActive,
        },
      )}
      prefetch={false}
    >
      {props.icon && (
        <div
          className={cn(
            'flex h-6 w-6 flex-col items-center justify-center',
            'ea-bottom-bar__item__icon-container',
            props.className?.iconContainer,
          )}
        >
          {props.icon}
        </div>
      )}
      {props.label && (
        <div
          className={cn(
            'text-center text-xs',
            'ea-bottom-bar__item__label',
            props.className?.label,
          )}
        >
          {t(props.label)}
        </div>
      )}
    </Link>
  );
}
